// src/components/enterprise/TriangulationPanel.jsx
//
// THE TRIANGULATION VIEW. One row per group, three legs side by side:
//   1. what members report (ERI, the Running Hot share, responses), observed,
//      aggregated at k >= 5 by the server;
//   2. what the organization's own systems say for that group (uploaded by
//      group, or org-wide when that is all there is), with the definition
//      questions that decide whether the figure is comparable;
//   3. the business average for the same metric.
// Under the table, "where the legs disagree": the readings that matter, in
// plain words. A leading signal concentrated in one unit; readiness falling
// where absence has not moved yet; an organizational figure so far from the
// average that the definition needs checking before the number is believed.
//
// Nothing here is a forecast. Every cell carries its source. When the org has
// no live snapshot the edition's modeled example stands in, badged as such.

import React, { useMemo } from 'react';
import { Users, Building2, Scale, AlertTriangle, Info, ChevronRight } from 'lucide-react';
import { useEdition } from '../../data/editions/EditionContext';
import { useOrganization } from '../../context/OrganizationContext';
import { SourceTag, ConfidenceTag, DisciplineFooter, MIN_GROUP_SIZE } from './shared/measurement';
import { METRIC_BY_ID, BEND_QUESTIONS, BUSINESS_INPUT_METRICS, packFor, referenceFor, formatMetricValue } from '../../data/workforceDictionary';

// Benchmarks exist for the five executive inputs; the dictionary id is the join.
const BENCH_BY_METRIC = Object.fromEntries(Object.entries(BUSINESS_INPUT_METRICS).map(([inputKey, metricId]) => [metricId, inputKey]));

const pct = (v) => (typeof v === 'number' ? `${Math.round(v * 100)}%` : '—');

const TriangulationPanel = ({ live, metrics, onNavigate }) => {
    const edition = useEdition();
    const { orgData } = useOrganization();
    const isLive = live?.source === 'live';
    const bench = metrics?.bench || {};
    const inputs = useMemo(() => metrics?.inputs || {}, [metrics]);
    const locale = edition.roi?.locale || 'en-CA';
    const currency = edition.roi?.currency || 'CAD';

    // The organizational metrics this sector leads with, trimmed to what fits a
    // row: the pack's order, except that a metric the organization has a figure
    // for comes before one it does not, so the table shows what is on file.
    const pack = useMemo(() => {
        const all = packFor(edition.id);
        const onFile = (id) => !!live?.business?.[id]
            || Object.values(live?.businessByGroup || {}).some((g) => g && g[id])
            || (BENCH_BY_METRIC[id] && inputs[BENCH_BY_METRIC[id]]?.source === 'client');
        return [...all.filter((m) => onFile(m.id)), ...all.filter((m) => !onFile(m.id))].slice(0, 4);
    }, [edition.id, live, inputs]);

    // Per-group organizational figures: uploaded by group when present, else org-wide.
    const orgFigure = (groupName, metricId) => {
        const byGroup = live?.businessByGroup?.[groupName]?.[metricId];
        if (byGroup) return { ...byGroup, scope: 'group' };
        const orgWide = live?.business?.[metricId];
        if (orgWide) return { ...orgWide, scope: 'org' };
        // The five executive inputs can also be typed by an admin (org-wide).
        const inputKey = BENCH_BY_METRIC[metricId];
        if (inputKey && inputs[inputKey] && inputs[inputKey].source === 'client') return { value: inputs[inputKey].value, source: 'typed', scope: 'org' };
        return null;
    };
    // The third leg: the executive benchmark where one exists, else the
    // sector reference from the dictionary; counts have neither.
    const average = (metricId) => {
        const inputKey = BENCH_BY_METRIC[metricId];
        if (inputKey && typeof bench[inputKey] === 'number') return bench[inputKey];
        return referenceFor(edition.id, metricId);
    };
    // Printed label for the third leg. No agency is named: these are conservative,
    // published-style values, illustrative until a sourced set for the sector lands.
    const refLabel = 'sector reference';
    const refHover = 'Illustrative reference value. Replaced by a sourced benchmark for this sector when one is on file.';
    // How far an organizational figure sits from its average, signed so that
    // positive is worse for the metric's direction; null when not comparable.
    const deltaFrom = (metric, value, avg) => {
        if (!metric || metric.direction === 'none' || typeof value !== 'number' || typeof avg !== 'number' || avg === 0) return null;
        const raw = Math.round(((value - avg) / Math.abs(avg)) * 100);
        return metric.direction === 'higher' ? -raw : raw;
    };

    // Rows: live groups, or the edition's modeled example.
    const rows = useMemo(() => {
        if (isLive && live.groups?.length) {
            return live.groups.map((g) => ({
                name: g.name, responses: g.responses, suppressed: !!g.suppressed, reason: g.suppressionReason,
                eri: typeof g.eri === 'number' ? g.eri : null,
                runningHot: typeof g.runningHotShare === 'number' ? g.runningHotShare : null,
                risk: typeof g.gamblingRiskShare === 'number' ? g.gamblingRiskShare : null,
                moderate: typeof g.gamblingModerateShare === 'number' ? g.gamblingModerateShare : null,
                pressure: typeof g.pressure === 'number' ? g.pressure : null
            }));
        }
        return (edition.readiness?.groups || []).map((g) => ({
            name: g.name, responses: g.responses, suppressed: g.responses < MIN_GROUP_SIZE,
            eri: null, runningHot: null, pressure: g.pressure, modeled: true
        }));
    }, [isLive, live, edition]);

    const orgRunningHot = isLive ? live.quadrants?.running_hot : null;
    const orgEri = isLive ? live.eri?.score : null;
    // A public program leads the members' leg with its screen (the gambling screen at 8+) instead of Running Hot.
    const popRisk = edition.populationRisk || null;
    const orgRisk = isLive && popRisk ? (live.gambling?.riskShare ?? null) : null;
    const leadShare = (r) => (popRisk ? r.risk : r.runningHot);
    const orgLeadShare = popRisk ? orgRisk : orgRunningHot;
    const leadLabel = popRisk ? popRisk.label : 'running hot';
    const concentratedText = (r) => (popRisk
        ? `${pct(r.risk)} ${popRisk.label} (${popRisk.detail}) against ${pct(orgRisk)} across the whole population. The need is concentrated here.`
        : `Running Hot share of ${pct(r.runningHot)} against ${pct(orgRunningHot)} across the organization. The leading signal is concentrated here.`);

    // Where the legs disagree.
    const findings = useMemo(() => {
        const out = [];
        const visible = rows.filter((r) => !r.suppressed);
        if (isLive) {
            visible.forEach((r) => {
                if (typeof leadShare(r) === 'number' && typeof orgLeadShare === 'number' && leadShare(r) >= orgLeadShare + (popRisk ? 0.05 : 0.10)) {
                    out.push({ tone: 'amber', group: r.name, text: concentratedText(r) });
                }
                if (typeof r.eri === 'number' && typeof orgEri === 'number' && r.eri <= orgEri - 8) {
                    const lagging = pack.map((m) => ({ m, f: orgFigure(r.name, m.id) })).find((x) => x.f && x.f.scope === 'group' && x.m.direction === 'lower');
                    out.push({ tone: 'amber', group: r.name, text: lagging
                        ? `Readiness ${r.eri} against ${orgEri} organization-wide, while ${lagging.m.label.toLowerCase()} for this group is ${formatMetricValue(lagging.m, lagging.f.value, locale, currency)}. If the second number has not moved yet, this is the pattern that moves first.`
                        : `Readiness ${r.eri} against ${orgEri} organization-wide, and no lagging figure for this group yet. Upload absence or sick calls by group to see whether they follow.` });
                }
            });
            // A unit's own figure far from the sector reference, and whether the members' leg agrees.
            visible.forEach((r) => {
                const worst = pack.map((m) => {
                    const f = orgFigure(r.name, m.id);
                    if (!f || f.scope !== 'group') return null;
                    const avg = average(m.id);
                    const worse = deltaFrom(m, f.value, avg);
                    return worse === null ? null : { m, f, avg, worse };
                }).filter((x) => x && x.worse >= 30).sort((a, b) => b.worse - a.worse)[0];
                if (!worst) return;
                const hot = typeof leadShare(r) === 'number' && typeof orgLeadShare === 'number' && leadShare(r) >= orgLeadShare + (popRisk ? 0.05 : 0.10);
                out.push({ tone: hot ? 'amber' : 'slate', group: r.name, text: `${worst.m.label} for this group is ${formatMetricValue(worst.m, worst.f.value, locale, currency)}, ${worst.worse}% ${worst.m.direction === 'higher' ? 'below' : 'above'} the ${refLabel} of ${formatMetricValue(worst.m, worst.avg, locale, currency)}.${hot ? ` People here also report the concentrated ${leadLabel} share: the two legs point the same way.` : ' People here report in line with the whole, so the figure may have a cause the check-ins do not see.'}` });
            });
            // Definition checks on the organization's own figures against the average.
            (metrics?.compare || []).forEach((c) => {
                if (c.key === 'eri' || c.source === 'benchmark' || c.deltaPct === null) return;
                if (Math.abs(c.deltaPct) >= 30) {
                    const metricId = BUSINESS_INPUT_METRICS[c.key];
                    const m = metricId ? METRIC_BY_ID[metricId] : null;
                    const q = m && m.bends.length ? BEND_QUESTIONS[m.bends[0]] : null;
                    out.push({ tone: 'slate', group: 'Organization', text: `${c.label} is ${Math.abs(c.deltaPct)}% ${c.deltaPct > 0 ? 'above' : 'below'} the average.${q ? ` Before reading that as performance, confirm the definition: ${q.text.toLowerCase()}` : ''}` });
                }
            });
            if (!out.length && visible.length) out.push({ tone: 'emerald', group: 'All groups', text: 'The three legs agree so far: no unit carries a concentrated leading signal, and no organizational figure sits far from its average.' });
        } else {
            const top = [...visible].sort((a, b) => (b.pressure || 0) - (a.pressure || 0))[0];
            if (top) out.push({ tone: 'amber', group: top.name, text: `Highest modeled strain (${top.pressure}/100). In a live organization this row would carry the unit's own sick calls and overtime beside it, and the sector average.` });
            out.push({ tone: 'slate', group: 'Organization', text: 'Modeled example. The first five scored check-ins in a group replace this with observed figures; a CSV by group fills the middle column.' });
        }
        return out;
    }, [rows, isLive, orgRunningHot, orgEri, metrics, pack, live, locale, currency]); // eslint-disable-line react-hooks/exhaustive-deps

    const suppressedCount = rows.filter((r) => r.suppressed).length;

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            {/* THE THREE LEGS, NAMED */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                    { Icon: Users, k: 'Leg 1 · observed, leading', t: 'What members report', d: isLive ? `${live.participation?.responded ?? '—'} members, last ${live.windowDays || 90} days, groups of ${MIN_GROUP_SIZE}+` : 'Modeled example until five members check in' },
                    { Icon: Building2, k: 'Leg 2 · the organization', t: 'What its systems say', d: isLive && live.business && Object.keys(live.business).length ? `${Object.keys(live.business).length} figure${Object.keys(live.business).length === 1 ? '' : 's'} on file${live.businessByGroup && Object.keys(live.businessByGroup).length ? `, ${Object.keys(live.businessByGroup).length} group${Object.keys(live.businessByGroup).length === 1 ? '' : 's'} broken out` : ', organization-wide only'}` : 'Nothing uploaded yet; typed inputs count' },
                    { Icon: Scale, k: 'Leg 3 · the comparison', t: 'The business average', d: 'Conservative sector references; illustrative until a sourced set for this sector is on file' }
                ].map(({ Icon, k, t, d }) => (
                    <div key={k} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-[var(--brand-blue)] shrink-0"><Icon size={18} /></div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{k}</p>
                            <p className="font-bold text-[var(--brand-deep)] leading-tight">{t}</p>
                            <p className="text-xs text-slate-500 mt-1 leading-snug">{d}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* THE TABLE */}
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-black text-[var(--brand-deep)]">By group, three legs side by side</h2>
                        <p className="text-xs text-slate-500 mt-0.5">Members' own readiness, the organization's figure for the same group, and the average. Groups under {MIN_GROUP_SIZE} are suppressed.</p>
                    </div>
                    <div className="flex items-center gap-2"><SourceTag modeled={!isLive} /><ConfidenceTag level="Leading" suffix="→ Lagging" /></div>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-6 py-3 font-bold">Group</th>
                                <th className="text-left px-4 py-3 font-bold"><span className="text-[var(--brand-blue)]">Members report</span></th>
                                {pack.map((m) => <th key={m.id} className="text-left px-4 py-3 font-bold">{m.label}</th>)}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r) => (
                                <tr key={r.name} className={r.suppressed ? 'bg-slate-50/60 text-slate-400' : ''}>
                                    <td className="px-6 py-3 align-top">
                                        <p className={`font-bold ${r.suppressed ? 'text-slate-400' : 'text-slate-800'}`}>{r.name}</p>
                                        <p className="text-[11px] text-slate-400">{r.suppressed ? (r.reason === 'complementary' ? 'Suppressed to protect a smaller group' : `Below the floor (${r.responses ?? '<5'})`) : `${r.responses} responses`}</p>
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        {r.suppressed ? <span>—</span> : r.modeled ? (
                                            <div><p className="font-bold text-slate-800 tabular-nums">Strain {r.pressure}/100</p><p className="text-[11px] text-slate-400">modeled</p></div>
                                        ) : (
                                            <div>
                                                <p className="font-bold text-slate-800 tabular-nums">ERI {r.eri ?? '—'}{typeof leadShare(r) === 'number' && <span className="font-medium text-amber-700"> · {pct(leadShare(r))} {leadLabel}</span>}{popRisk && typeof r.moderate === 'number' && <span className="font-medium text-slate-500"> · {pct(r.moderate)} {popRisk.moderateLabel}</span>}</p>
                                                <p className="text-[11px] text-slate-400">observed, k ≥ {MIN_GROUP_SIZE}{popRisk ? ` · ${popRisk.detail}` : ''}</p>
                                            </div>
                                        )}
                                    </td>
                                    {pack.map((m) => {
                                        // The organization's own figures are not member data: they stay
                                        // on a suppressed row (muted); only the members' leg is withheld.
                                        const f = orgFigure(r.name, m.id);
                                        const avg = average(m.id);
                                        const worse = f ? deltaFrom(m, f.value, avg) : null;
                                        const shown = worse === null ? null : (m.direction === 'higher' ? -worse : worse);
                                        return (
                                            <td key={m.id} className="px-4 py-3 align-top">
                                                {f ? (
                                                    <div>
                                                        <p className={`font-bold tabular-nums ${r.suppressed ? 'text-slate-500' : 'text-slate-800'}`}>
                                                            {formatMetricValue(m, f.value, locale, currency)}
                                                            {shown !== null && (
                                                                <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md align-middle whitespace-nowrap ${worse >= 30 ? 'bg-amber-50 text-amber-700' : worse <= -10 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{shown > 0 ? '+' : ''}{shown}% vs avg</span>
                                                            )}
                                                        </p>
                                                        <p className="text-[11px] text-slate-400" title={f.asOf ? `as of ${String(f.asOf).slice(0, 10)}` : undefined}>{f.scope === 'group' ? 'this group' : 'organization-wide'}{f.source && f.source !== 'typed' ? ` · ${f.source}` : f.source === 'typed' ? ' · typed' : ''}</p>
                                                    </div>
                                                ) : (
                                                    <p className="text-[11px] text-slate-400 leading-snug">not on file</p>
                                                )}
                                                <p className="text-[11px] text-slate-500 mt-1" title={avg !== null ? refHover : undefined}>{avg !== null ? `avg ${formatMetricValue(m, avg, locale, currency)} · ${refLabel}` : 'a count: no sector average'}</p>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-6 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                    <span>{suppressedCount ? `${suppressedCount} group${suppressedCount === 1 ? '' : 's'} suppressed and rolled into the organization-wide figures.` : 'No group suppressed.'}</span>
                    <span>Figures by group arrive through Data Intake: typed one at a time, or a file with a group column. Rates and per-FTE figures carry a {refLabel}; counts do not, because a count needs the group's size before it compares.</span>
                </div>
            </div>

            {/* WHERE THE LEGS DISAGREE */}
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-lg font-black text-[var(--brand-deep)]">Where the legs disagree</h2>
                        <p className="text-xs text-slate-500 mt-0.5">The readings worth a conversation. Associations, never forecasts.</p>
                    </div>
                    <SourceTag modeled={!isLive} />
                </div>
                <ul className="space-y-3">
                    {findings.map((f, i) => (
                        <li key={i} className={`flex items-start gap-3 p-4 rounded-2xl border ${f.tone === 'amber' ? 'bg-amber-50/60 border-amber-100' : f.tone === 'emerald' ? 'bg-emerald-50/60 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
                            {f.tone === 'amber' ? <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" /> : <Info size={18} className="text-slate-500 shrink-0 mt-0.5" />}
                            <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{f.group}</p>
                                <p className="text-sm text-slate-800 leading-relaxed">{f.text}</p>
                            </div>
                        </li>
                    ))}
                </ul>
                <div className="mt-5 flex flex-wrap gap-2">
                    <button type="button" onClick={() => onNavigate && onNavigate('cc-readiness')} className="inline-flex items-center gap-1 text-xs font-bold text-[var(--brand-blue)] hover:underline">Readiness detail <ChevronRight size={14} /></button>
                    <button type="button" onClick={() => onNavigate && onNavigate('cc-data')} className="inline-flex items-center gap-1 text-xs font-bold text-[var(--brand-blue)] hover:underline">Bring the organization's figures in <ChevronRight size={14} /></button>
                </div>
            </div>

            <DisciplineFooter caption={`Leg 1 is observed and leading; legs 2 and 3 are lagging context. ${orgData?.name || 'The organization'} keeps its own figures; EAS labels where each came from and asks how it was defined before comparing it.`} />
        </div>
    );
};

export default TriangulationPanel;
